import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ArrowPathIcon, SparklesIcon } from '@heroicons/react/16/solid';
import { Dialog } from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import {
  AmenityReturn,
  HoaReturn,
  ReservationRuleViolation,
  ReservationSeriesFrequency,
  ReservationSeriesResult,
  ReservationSeriesReturn,
} from '../clients/rust-api-client';
import { amenityService, reservationService } from '../clients/ApiClients';
import { formatMoney } from '../utils/billingHelpers';
import { PropertyPicker } from '../violation/linear/PropertyPicker';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { CreateScope } from '../shell/createScope';
import { ScopeChips, useForHoa, useScopeState } from '../shell/ScopeChips';
import { RuleViolationsPanel } from './RuleViolationsPanel';
import {
  FREQUENCY_LABELS,
  MAX_OCCURRENCES,
  MONTH_WEEK_LABELS,
  SeriesForm,
  buildSeriesRequest,
  seriesFormProblem,
  summarizeSeriesResult,
} from './seriesMeta';
import {
  WEEKDAYS_SHORT,
  formatDuration,
  formatLocalRange,
  hhmmToMinute,
  isZeroMoney,
  minuteToHHMM,
  reservationErrorMessage,
  ruleLabel,
  todayIn,
  tzLabel,
} from './reservationMeta';
import { inputClass, labelClass } from './formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * A recurring booking for one household (P2, #642) — a league night, a
 * standing Tuesday court. Staff only.
 *
 * Two steps, so nothing is booked blind: **Preview** asks the server for
 * every occurrence and the rules each would break (a dry run), then
 * **Book** books the ones that pass. An occurrence that breaks a rule is
 * skipped, not forced — unless the manager overrides that rule, per code
 * and with one reason, for the whole series.
 */
interface Props {
  open: boolean;
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  scope?: CreateScope;
  lockScope?: boolean;
  onClose: () => void;
  onCreated: (series: ReservationSeriesReturn) => void;
}

const fetchAmenities = async (hoaId: string): Promise<AmenityReturn[]> =>
  (await amenityService.listAmenities({ hoaId })).data.amenities
    .filter(
      (a) =>
        a.is_active && (a.booking_mode === 'exclusive' || a.booking_mode === 'shared_capacity')
    )
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));

const emptyForm = (today: string): SeriesForm => ({
  amenityId: null,
  homeId: null,
  frequency: 'weekly',
  weekdays: [],
  monthWeek: 1,
  startMinute: null,
  durationMinutes: null,
  startsOn: today,
  endsBy: 'count',
  untilOn: '',
  count: '8',
  partySize: '1',
  purpose: '',
  internalNotes: '',
});

export const CreateSeriesModal: React.FC<Props> = ({
  open,
  hoas,
  hoasLoading,
  scope = {},
  lockScope,
  onClose,
  onCreated,
}) => {
  const { hoaId, setHoaId, homeId, setHomeId } = useScopeState(open, scope, hoas);
  const amenities = useForHoa(hoaId, fetchAmenities, 'amenities');
  const [form, setForm] = useState<SeriesForm>(() => emptyForm(todayIn(null)));
  const [preview, setPreview] = useState<ReservationSeriesResult | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState<'preview' | 'book' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const amenity = amenities.items.find((a) => a.id === form.amenityId) ?? null;
  const set = (patch: Partial<SeriesForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    // Anything changed makes the preview stale.
    setPreview(null);
  };

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm(todayIn(null)));
    setPreview(null);
    setTicked([]);
    setOverrideReason('');
    setError(null);
  }, [open]);

  // The household comes from the scope chips.
  useEffect(() => {
    setForm((f) => (f.homeId === homeId ? f : { ...f, homeId }));
    setPreview(null);
  }, [homeId]);

  // An amenity sets the defaults for time, length and the first day.
  useEffect(() => {
    if (!amenity) return;
    setForm((f) => ({
      ...f,
      startsOn: todayIn(amenity.timezone),
      durationMinutes:
        f.durationMinutes &&
        f.durationMinutes >= amenity.min_duration_minutes &&
        f.durationMinutes <= amenity.max_duration_minutes
          ? f.durationMinutes
          : amenity.min_duration_minutes,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenity?.id]);

  const durations = useMemo(() => {
    if (!amenity) return [];
    const out: number[] = [];
    for (let m = amenity.min_duration_minutes; m <= amenity.max_duration_minutes; m += amenity.slot_increment_minutes)
      out.push(m);
    return out;
  }, [amenity]);

  const problem = seriesFormProblem(form);
  const summary = preview ? summarizeSeriesResult(preview) : null;
  // The rules that skip occurrences, as violations the override panel can tick.
  const skipViolations: ReservationRuleViolation[] = summary
    ? [
        ...summary.overridable.map((code) => ({
          code: code as ReservationRuleViolation['code'],
          overridable: true,
          message: `skips ${preview!.occurrences.filter((o) => o.violations.some((v) => v.code === code)).length} occurrence(s)`,
        })),
        ...summary.hard.map((code) => ({
          code: code as ReservationRuleViolation['code'],
          overridable: false,
          message: `skips ${preview!.occurrences.filter((o) => o.violations.some((v) => v.code === code)).length} occurrence(s)`,
        })),
      ]
    : [];
  const overridesOk = ticked.length === 0 || overrideReason.trim().length > 0;

  const run = async (dryRun: boolean) => {
    const body = buildSeriesRequest(form, { dryRun, overrides: ticked, overrideReason });
    if (!body) return;
    setBusy(dryRun ? 'preview' : 'book');
    setError(null);
    try {
      const res = await reservationService.createReservationSeries({ reservationSeriesCreate: body });
      if (dryRun) {
        setPreview(res.data);
        setTicked((t) => t.filter((c) => summarizeSeriesResult(res.data).overridable.includes(c)));
      } else if (res.data.series) {
        onCreated(res.data.series);
        onClose();
      } else {
        setPreview(res.data);
        setError('Nothing was booked — every occurrence breaks a rule.');
      }
    } catch (err) {
      setError(reservationErrorMessage(err, dryRun ? "Couldn't preview the series." : "Couldn't book the series."));
    } finally {
      setBusy(null);
    }
  };

  const monthly = form.frequency === 'monthly_nth_weekday';
  const toggleDay = (d: number) =>
    set({
      weekdays: monthly
        ? [d]
        : form.weekdays.includes(d)
          ? form.weekdays.filter((x) => x !== d)
          : [...form.weekdays, d],
    });

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <form
        aria-label="New recurring reservation"
        onSubmit={(e) => {
          e.preventDefault();
          run(!preview);
        }}
      >
        <p className="text-[12px] font-medium text-fg-muted">New recurring reservation</p>
        <p className="mt-2 text-xl font-semibold text-fg">{amenity ? amenity.name : 'Book a standing time for a household'}</p>
        <p className="mt-1 text-[13px] text-fg-muted">
          One booking per occurrence, each checked against the amenity’s rules and approved on creation. An
          occurrence that breaks a rule is skipped unless you override it.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <ScopeChips
            hoas={hoas}
            hoasLoading={hoasLoading}
            hoaId={hoaId}
            onHoaChange={setHoaId}
            lockHoa={lockScope && !!scope.hoaId}
            memberId={scope.memberId}
            home={{ homeId, onChange: setHomeId, locked: lockScope && !!scope.homeId }}
          />
          <PropertyPicker
            options={amenities.items.map((a) => ({ value: a.id, label: a.name }))}
            value={form.amenityId}
            onChange={(id) => set({ amenityId: id })}
            placeholder="Amenity…"
            ariaLabel={amenity ? `Amenity: ${amenity.name}` : 'Choose amenity'}
            readOnly={!hoaId || amenities.loading}
            trigger={
              <>
                <SparklesIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {amenity ? amenity.name : <span className="text-fg-muted">Amenity</span>}
              </>
            }
          />
        </div>

        {amenity && (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className={labelClass}>
                Repeats
                <FieldPicker
                  size="sm"
                  className="w-52"
                  label="Repeats"
                  value={form.frequency}
                  onChange={(v) => {
                    const frequency = v as ReservationSeriesFrequency;
                    set({
                      frequency,
                      weekdays: frequency === 'monthly_nth_weekday' ? form.weekdays.slice(0, 1) : form.weekdays,
                    });
                  }}
                  options={(Object.keys(FREQUENCY_LABELS) as ReservationSeriesFrequency[]).map((f) => ({
                    value: f,
                    label: FREQUENCY_LABELS[f],
                  }))}
                />
              </div>
              {monthly && (
                <div className={labelClass}>
                  Which
                  <FieldPicker
                    size="sm"
                    className="w-28"
                    label="Which"
                    value={String(form.monthWeek)}
                    onChange={(v) => set({ monthWeek: Number(v) })}
                    options={[1, 2, 3, 4, -1].map((n) => ({ value: String(n), label: MONTH_WEEK_LABELS[n] }))}
                  />
                </div>
              )}
              <fieldset>
                <legend className="mb-1 text-[12px] font-medium text-fg-muted">{monthly ? 'Weekday' : 'On'}</legend>
                <div className="flex gap-1">
                  {WEEKDAYS_SHORT.map((name, d) => (
                    <button
                      key={name}
                      type="button"
                      aria-pressed={form.weekdays.includes(d)}
                      onClick={() => toggleDay(d)}
                      className={clsx(
                        'rounded-md border px-2 py-1 text-[12px]',
                        form.weekdays.includes(d)
                          ? 'border-accent bg-accent/15 text-fg'
                          : 'border-line text-fg-muted hover:bg-fg/5'
                      )}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className={labelClass}>
                Starts at
                <input
                  type="time"
                  step={amenity.slot_increment_minutes * 60}
                  value={form.startMinute === null ? '' : minuteToHHMM(form.startMinute)}
                  onChange={(e) => set({ startMinute: hhmmToMinute(e.target.value) })}
                  className={inputClass}
                />
              </label>
              <div className={labelClass}>
                Length
                <FieldPicker
                  size="sm"
                  className="w-36"
                  label="Length"
                  value={form.durationMinutes == null ? null : String(form.durationMinutes)}
                  onChange={(v) => set({ durationMinutes: Number(v) })}
                  options={durations.map((d) => ({ value: String(d), label: formatDuration(d) }))}
                />
              </div>
              <label className={labelClass}>
                First day
                <input
                  type="date"
                  value={form.startsOn}
                  onChange={(e) => set({ startsOn: e.target.value })}
                  className={inputClass}
                />
              </label>
              <div className={labelClass}>
                Ends
                <FieldPicker
                  size="sm"
                  className="w-52"
                  label="Ends"
                  value={form.endsBy}
                  onChange={(v) => set({ endsBy: v as SeriesForm['endsBy'] })}
                  options={[
                    { value: 'count', label: 'After a number of times' },
                    { value: 'date', label: 'On a date' },
                  ]}
                />
              </div>
              {form.endsBy === 'count' ? (
                <label className={labelClass}>
                  Times
                  <input
                    inputMode="numeric"
                    value={form.count}
                    onChange={(e) => set({ count: e.target.value.replace(/\D/g, '') })}
                    className={clsx(inputClass, 'w-20')}
                  />
                </label>
              ) : (
                <label className={labelClass}>
                  Last day
                  <input
                    type="date"
                    value={form.untilOn}
                    onChange={(e) => set({ untilOn: e.target.value })}
                    className={inputClass}
                  />
                </label>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-[6rem_1fr_1fr]">
              <label className={labelClass}>
                Party size
                <input
                  inputMode="numeric"
                  value={form.partySize}
                  onChange={(e) => set({ partySize: e.target.value.replace(/\D/g, '') })}
                  className={inputClass}
                />
              </label>
              <label className={labelClass}>
                Purpose (optional)
                <input
                  value={form.purpose}
                  onChange={(e) => set({ purpose: e.target.value })}
                  placeholder="e.g. Tuesday league"
                  className={inputClass}
                />
              </label>
              <label className={labelClass}>
                Internal note (managers only)
                <input
                  value={form.internalNotes}
                  onChange={(e) => set({ internalNotes: e.target.value })}
                  className={inputClass}
                />
              </label>
            </div>
            <p className="text-[12px] text-fg-muted">
              {tzLabel(amenity.timezone)} · at most {MAX_OCCURRENCES} occurrences.
            </p>
          </div>
        )}

        {preview && summary && (
          <div className="mt-4 space-y-3 border-t border-line/60 pt-3">
            <p className="text-[13px] font-medium text-fg">
              {summary.wouldBook} of {preview.occurrences.length} would be booked
              {summary.wouldSkip > 0 && `, ${summary.wouldSkip} skipped`}.
            </p>
            <ul className="max-h-56 divide-y divide-line/50 overflow-y-auto rounded-lg border border-line text-[12px]">
              {preview.occurrences.map((o) => (
                <li key={o.start_local} className="flex items-start gap-2 px-2.5 py-1.5">
                  <span
                    className={clsx(
                      'mt-0.5 shrink-0 rounded px-1 text-[10px] font-medium uppercase',
                      o.violations.length ? 'bg-warning/15 text-warning' : 'bg-accent/15 text-accent-fg'
                    )}
                  >
                    {o.violations.length ? 'Skip' : 'Book'}
                  </span>
                  <span className="min-w-0">
                    <span className="text-fg-secondary">{formatLocalRange(o.start_local, o.end_local)}</span>
                    {!isZeroMoney(o.fee) && <span className="text-fg-muted"> · {formatMoney(o.fee)}</span>}
                    {o.violations.length > 0 && (
                      <span className="block text-fg-muted">{o.violations.map((v) => ruleLabel(v.code)).join(', ')}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <RuleViolationsPanel
              idPrefix="series"
              violations={skipViolations}
              ticked={ticked}
              onTickedChange={(codes) => {
                setTicked(codes);
              }}
              reason={overrideReason}
              onReasonChange={setOverrideReason}
            />
            {ticked.length > 0 && (
              <p className="text-[12px] text-fg-muted">
                Preview again to see which occurrences the overrides let through.
              </p>
            )}
          </div>
        )}

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-line/60 pt-4">
          {amenity && problem && <span className="mr-auto text-[12px] text-fg-muted">{problem}</span>}
          <Button plain type="button" onClick={onClose} disabled={!!busy}>
            Cancel
          </Button>
          <Button outline type="button" onClick={() => run(true)} disabled={!!problem || !!busy || !overridesOk}>
            <ArrowPathIcon data-slot="icon" />
            {busy === 'preview' ? 'Checking…' : preview ? 'Preview again' : 'Preview'}
          </Button>
          <Button
            color="sky"
            type="button"
            onClick={() => run(false)}
            disabled={!preview || !!problem || !!busy || !overridesOk || (summary?.wouldBook ?? 0) === 0}
          >
            {busy === 'book'
              ? 'Booking…'
              : summary
                ? `Book ${summary.wouldBook} occurrence${summary.wouldBook === 1 ? '' : 's'}`
                : 'Book'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default CreateSeriesModal;
